import React, { useState, useEffect } from 'react';
import { getFirestore, collection, addDoc, doc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { FaTimes, FaImage, FaSave } from 'react-icons/fa';

const ProgramModal = ({ isOpen, onClose, editingFolder = null }) => {
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(false);

  const db = getFirestore();
  const storage = getStorage();

  // Pre-fill if editing
  useEffect(() => {
    if (editingFolder) {
      setName(editingFolder.programName || '');
      // Formatting timestamp for datetime-local input
      if (editingFolder.startTime) {
        const date = editingFolder.startTime.toDate();
        setStartTime(new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
      }
    } else {
      setName('');
      setStartTime('');
    }
  }, [editingFolder]);

  const handleSave = async () => {
    if (!name || !startTime) return alert("Program Name and Start Time are required.");
    setLoading(true);

    try {
      let artworkURL = editingFolder?.artworkURL || null;

      // 1. Handle New Artwork Upload if provided
      if (artwork) {
        const artRef = ref(storage, `radio/artworks/${Date.now()}_${artwork.name}`);
        artworkURL = await uploadBytesResumable(artRef, artwork).then(s => getDownloadURL(s.ref));
      }

      const programData = {
        programName: name,
        startTime: Timestamp.fromDate(new Date(startTime)),
        artworkURL,
        isActive: true,
        lastUpdated: serverTimestamp(),
      };

      if (editingFolder) {
        // UPDATE EXISTING
        await updateDoc(doc(db, 'radio_folders', editingFolder.id), programData);
      } else {
        // CREATE NEW
        await addDoc(collection(db, 'radio_folders'), {
          ...programData,
          createdAt: serverTimestamp(),
        });
      }

      onClose();
    } catch (err) {
      console.error("Save Error:", err);
      alert("Failed to save program.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden">
        <header className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h3 className="text-xl font-black text-slate-800">{editingFolder ? 'Edit Program' : 'New Program'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><FaTimes /></button>
        </header>

        <div className="p-6 space-y-5">
          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase text-slate-400">Program Name</label>
            <input 
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-purple-500"
              placeholder="e.g. 100 Song Countdown"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase text-slate-400">Broadcast Start Time</label>
            <input 
              type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)}
              className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-purple-500"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase text-slate-400">Album Artwork</label>
            <div className="relative group cursor-pointer bg-slate-50 rounded-2xl p-8 border-2 border-dashed border-slate-200 flex flex-col items-center justify-center hover:border-purple-300">
              <input type="file" accept="image/*" onChange={(e) => setArtwork(e.target.files[0])} className="absolute inset-0 opacity-0 cursor-pointer" />
              <FaImage className="text-slate-300 text-3xl mb-2" />
              <p className="text-xs font-bold text-slate-400">{artwork ? artwork.name : 'Upload New Art'}</p>
            </div>
          </div>
        </div>

        <footer className="p-6 bg-slate-50">
          <button 
            onClick={handleSave} disabled={loading}
            className="w-full py-4 bg-purple-600 text-white rounded-2xl font-black uppercase tracking-widest text-sm shadow-lg shadow-purple-100 flex items-center justify-center gap-2"
          >
            {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <FaSave />}
            {editingFolder ? 'Update Metadata' : 'Create Folder'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ProgramModal;